"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Calendar, Phone, ArrowUpRight, Clock, ShieldCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function Action() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="call-to-action" data-nav="dark" className="relative overflow-hidden bg-secondary py-20 text-secondary-foreground md:py-24">
      {/* Ambient warm glow decorative elements */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-24 left-1/2 h-96 w-96 -translate-x-1/2 rounded-full bg-primary/15 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-24 right-1/4 h-80 w-80 rounded-full bg-primary/10 blur-3xl" />

      <div className="relative mx-auto max-w-5xl px-5 sm:px-8">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }} className="relative rounded-2xl border border-white/15 bg-white/[0.07] p-8 shadow-2xl backdrop-blur-md sm:p-12 md:p-16">
          {/* Subtle gold accent hairline across card top */}
          <div aria-hidden="true" className="absolute inset-x-8 top-0 h-[1px] bg-gradient-to-r from-transparent via-primary/50 to-transparent sm:inset-x-16" />

          <div className="flex flex-col items-center text-center">
            {/* Eyebrow badge */}
            <div className="mb-6 inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-3.5 py-1 text-xs font-semibold tracking-wider text-primary uppercase">
              <ShieldCheck className="size-3.5 text-primary" />
              <span>START YOUR TRANSFORMATION</span>
            </div>

            {/* Playfair Display Heading */}
            <h2 className="font-serif text-3xl font-semibold tracking-tight text-white sm:text-4xl md:text-5xl">
              Schedule your Brookhaven home walkthrough
            </h2>

            {/* Architectural Gold Hairline divider */}
            <div aria-hidden="true" className="my-6 h-[1px] w-24 bg-primary/60" />

            {/* Supporting Copy */}
            <p className="max-w-2xl text-base text-secondary-foreground/80 sm:text-lg">
              Meet on site to explore layout options, initial budgeting, and clear timeline milestones — we will follow up to discuss your project.
            </p>

            {/* Action Group */}
            <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row sm:gap-6">
              <motion.div whileHover={shouldReduceMotion ? {} : {
              y: -4
            }} transition={{
              duration: 0.25,
              ease: [0.2, 0.8, 0.2, 1]
            }} className="w-full sm:w-auto">
                <Button asChild size="lg" className="w-full border border-primary/80 bg-primary px-8 py-3.5 text-base font-semibold text-primary-foreground shadow-lg transition-[background-color,border-color,box-shadow] hover:bg-primary/90 hover:shadow-primary/20 sm:w-auto">
                  <Link to="/ContactRequestAnEstimatePage" className="inline-flex items-center justify-center gap-2">
                    <SafeHydrate><Calendar className="size-4" /></SafeHydrate>
                    <span>Request an Estimate</span>
                    <ArrowUpRight className="size-4" />
                  </Link>
                </Button>
              </motion.div>

              <a href="tel:+14708479076" className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/5 px-6 py-3 text-sm font-medium text-white transition-[background-color,border-color] hover:border-primary/60 hover:bg-white/10">
                <Phone className="size-4 text-primary" />
                <span>Call (470) 847-9076</span>
              </a>
            </div>

            {/* Secondary Guide Links */}
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3 text-xs sm:gap-4 sm:text-sm">
              <Link to="#" className="inline-flex items-center gap-1.5 rounded-lg border border-white/15 bg-white/[0.03] px-3.5 py-1.5 font-medium text-secondary-foreground/90 transition-[border-color,background-color,color] hover:border-primary/50 hover:bg-white/[0.08] hover:text-white">
                <span>Read our full whole-home remodeling guide</span>
                <ArrowUpRight className="size-3.5 text-primary" />
              </Link>
              <Link to="#" className="inline-flex items-center gap-1.5 rounded-lg border border-white/15 bg-white/[0.03] px-3.5 py-1.5 font-medium text-secondary-foreground/90 transition-[border-color,background-color,color] hover:border-primary/50 hover:bg-white/[0.08] hover:text-white">
                <span>Read our interior remodeling guide</span>
                <ArrowUpRight className="size-3.5 text-primary" />
              </Link>
            </div>

            {/* Reassurance meta row */}
            <div className="mt-10 flex flex-wrap items-center justify-center gap-6 border-t border-white/10 pt-6 text-xs text-secondary-foreground/70 sm:text-sm">
              <div className="inline-flex items-center gap-2">
                <Clock className="size-4 text-primary" />
                <span>Consultation scheduled within 3 business days</span>
              </div>
              <div className="hidden h-1 w-1 rounded-full bg-white/30 sm:block" />
              <div className="inline-flex items-center gap-2">
                <span>Detailed scope and transparent budget breakdown</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}